/**
 * Turning Codex auth state into something renderable.
 *
 * Pure: every function here maps a session, a credential or an account to the
 * words and flags the UI shows. Kept out of the component because these are the
 * parts worth testing directly, and they are testable only while they stay free
 * of React.
 *
 * @module
 */
import { CmmdCodexCredentialState } from "../../environments/cmmdApi";
import {
  maskConnectedAccountLabel,
  resolveConnectedAccountEmail,
} from "../../lib/connectedAccountEmail";
import { isProviderAuthSessionTerminal } from "./providerProvenance";
import { ProviderAuthSession, ServerProvider } from "@cmmd-forge/contracts";
import { UnifiedSettings } from "@cmmd-forge/contracts/settings";

export const formatCodexAuthStartError = (error: unknown): string => {
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : String(error);
  if (/All fibers interrupted without error/i.test(message)) {
    return "Forge is still starting your project. Wait a moment, then try ChatGPT sign-in again.";
  }
  return message || "Could not start Codex account sign-in.";
};

export type CodexAuthFailureViewModel =
  | {
      readonly kind: "codex_cli_missing";
      readonly title: string;
      readonly detail: string;
      readonly guidanceTitle: string;
      readonly guidance: string;
      readonly action: "repair_retry";
    }
  | {
      readonly kind: "codex_device_auth_disabled";
      readonly title: string;
      readonly detail: string;
      readonly guidanceTitle: string;
      readonly guidance: string;
      readonly action: "security_settings";
    }
  | {
      readonly kind: "expired" | "generic";
      readonly title: string;
      readonly detail: string;
      readonly guidanceTitle: string;
      readonly guidance: string;
      readonly action: "retry";
    }
  | {
      readonly kind: "codex_unsupported_surface";
      readonly title: string;
      readonly detail: string;
      readonly guidanceTitle: string;
      readonly guidance: string;
      readonly action: "none";
    };

export function getCodexAuthFailureViewModel(
  authSession: ProviderAuthSession | null,
): CodexAuthFailureViewModel {
  if (authSession?.issue === "provider_cli_missing") {
    return {
      kind: "codex_cli_missing",
      title: "Codex CLI unavailable",
      detail:
        authSession.message ??
        "Codex CLI is not installed or is not available in this Forge runtime.",
      guidanceTitle: "Runtime repair",
      guidance:
        "Forge needs the Codex CLI installed before it can start ChatGPT device authorization. Forge can install or update Codex, refresh provider status, then retry sign-in.",
      action: "repair_retry",
    };
  }

  if (authSession?.issue === "provider_device_auth_disabled") {
    return {
      kind: "codex_device_auth_disabled",
      title: "Device authorization disabled",
      detail:
        authSession.message ??
        "OpenAI says device authorization is disabled for this account or organization.",
      guidanceTitle: "Before trying again",
      guidance:
        "Enable device authorization in ChatGPT Security Settings for the account or organization you are signing in with.",
      action: "security_settings",
    };
  }

  if (authSession?.state === "expired") {
    return {
      kind: "expired",
      title: "Codex code expired",
      detail:
        authSession.message ??
        "That Codex sign-in code expired. Start a new sign-in to get a fresh code.",
      guidanceTitle: "Get a fresh code",
      guidance: "Start a new Codex sign-in session and complete it before the code expires.",
      action: "retry",
    };
  }

  if (authSession?.issue === "provider_auth_unsupported_surface") {
    return {
      kind: "codex_unsupported_surface",
      title: "Sign-in isn't available here",
      detail: authSession.message ?? "This Forge surface can't connect a personal subscription.",
      guidanceTitle: "Where to sign in",
      guidance:
        "Personal ChatGPT sign-in runs on your own runtime or the desktop app, where the credential stays scoped to you. Open Forge there to connect your account.",
      action: "none",
    };
  }

  return {
    kind: "generic",
    title: "Codex sign-in needs attention",
    detail: authSession?.message ?? "Forge could not complete Codex sign-in.",
    guidanceTitle: "Before trying again",
    guidance: "Refresh provider status, then start ChatGPT sign-in again.",
    action: "retry",
  };
}

export function shouldUseCodexCloudSignIn(input: {
  readonly authSession: ProviderAuthSession | null;
  readonly cloudSignInAvailable: boolean;
}): boolean {
  return (
    input.cloudSignInAvailable &&
    input.authSession?.state === "failed" &&
    input.authSession.issue === "provider_auth_unsupported_surface"
  );
}

export function formatCodexPlanLabel(label: string | null | undefined): string | null {
  const trimmed = label?.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/[_-]+/g, " ");
  const core = normalized.replace(/^(?:chatgpt|claude)\s+/iu, "").replace(/\s+subscription$/iu, "");
  if (!core || core.toLowerCase() === "unknown") return null;
  return core
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export type CodexAuthManualCopyValues = {
  readonly code?: string;
  readonly link?: string;
};

export function formatDeviceCodeForSplitInput(code: string | undefined): string | null {
  const normalized = code?.replace(/-/gu, "").trim();
  return normalized ? normalized : null;
}

export function resolveCodexAccountIdentity(
  provider: ServerProvider | undefined,
  cloudCredential: CmmdCodexCredentialState | null | undefined,
): {
  readonly connectedViaCloudStore: boolean;
  readonly hasUserCodexAccount: boolean;
  readonly codexAccountLabel: string | null;
  readonly codexSubscriptionLabel: string | null;
  readonly needsReconnect: boolean;
} {
  if (provider?.auth.status === "authenticated") {
    return {
      connectedViaCloudStore: false,
      hasUserCodexAccount: true,
      codexAccountLabel:
        provider.auth.accountEmail ?? provider.auth.provenance?.accountLabel ?? null,
      codexSubscriptionLabel: provider.auth.label ?? null,
      needsReconnect: false,
    };
  }
  if (cloudCredential?.connected === true) {
    return {
      connectedViaCloudStore: true,
      hasUserCodexAccount: true,
      codexAccountLabel: cloudCredential.accountEmail,
      codexSubscriptionLabel: cloudCredential.planType,
      needsReconnect: cloudCredential.needsReconnect,
    };
  }
  return {
    connectedViaCloudStore: false,
    hasUserCodexAccount: false,
    codexAccountLabel: null,
    codexSubscriptionLabel: null,
    needsReconnect: false,
  };
}

export function getCodexAuthAccessSourceViewModel(input: {
  readonly provider: ServerProvider | undefined;
  readonly accessMode: UnifiedSettings["providers"]["codex"]["accessMode"];
  readonly authSession: ProviderAuthSession | null;
  readonly cloudSignInAvailable?: boolean;
  // Card 1359 — the credential in the CMMD USER store, for cloud-only surfaces
  // where the local codex CLI is never "authenticated" but a harvested personal
  // subscription lives server-side and is used via the egress proxy.
  readonly cloudCredential?: CmmdCodexCredentialState | null;
}) {
  const {
    provider,
    accessMode,
    authSession,
    cloudCredential,
    cloudSignInAvailable = false,
  } = input;
  const isWorkspaceOnline = provider?.enabled !== false && provider?.status !== "error";
  const {
    connectedViaCloudStore,
    hasUserCodexAccount,
    codexAccountLabel,
    codexSubscriptionLabel,
    needsReconnect,
  } = resolveCodexAccountIdentity(provider, cloudCredential);
  const isUsingChatGptAccount = hasUserCodexAccount && accessMode === "personal";
  const codexAccountDescription = hasUserCodexAccount
    ? maskConnectedAccountLabel(codexAccountLabel)
    : "Use your own ChatGPT account.";
  // The Manage dialog shows the address in full: it is the user's own account,
  // behind a deliberate click, and identifying it is the entire point of the
  // dialog. The always-visible row above stays masked.
  const codexAccountEmail = resolveConnectedAccountEmail(codexAccountLabel);
  const codexPlanLabel = formatCodexPlanLabel(codexSubscriptionLabel);
  // Where the credential lives decides what we can honestly promise about reach.
  const codexCredentialScopeLabel = connectedViaCloudStore
    ? "Stored in your CMMD account. Available to your cloud runtimes."
    : "Stored on this device. Cloud VMs are not delegated.";
  const defaultScopeLabel = "Default for new chats";
  const existingChatResumeLabel =
    "Existing chats resume only while their saved account identity still matches. After switching accounts, switch back or start a new chat.";
  const workspaceAccessDescription = isWorkspaceOnline
    ? "Organization-managed access for new Codex chats."
    : "Workspace access unavailable.";
  const personalAccessDescription = hasUserCodexAccount
    ? connectedViaCloudStore
      ? `${codexAccountDescription} is available to your cloud runtimes.`
      : `${codexAccountDescription} is available on this device.`
    : cloudSignInAvailable
      ? "Connect ChatGPT for new Codex chats in your cloud runtimes."
      : "Connect your ChatGPT account for new Codex chats on this device.";
  const isAuthSessionActive = authSession !== null && !isProviderAuthSessionTerminal(authSession);
  const isAuthReadyForUser = authSession?.state === "waiting_for_user";
  const isAuthVerifying = authSession?.state === "completed";
  const isAuthFailed =
    authSession?.state === "failed" ||
    authSession?.state === "canceled" ||
    authSession?.state === "expired";
  const normalizedUserCode = formatDeviceCodeForSplitInput(authSession?.userCode);

  return {
    codexAccountDescription,
    codexAccountEmail,
    codexAccountLabel,
    codexCredentialScopeLabel,
    codexPlanLabel,
    codexSubscriptionLabel,
    defaultScopeLabel,
    existingChatResumeLabel,
    hasUserCodexAccount,
    connectedViaCloudStore,
    needsReconnect,
    isAuthFailed,
    isAuthReadyForUser,
    isAuthSessionActive,
    isAuthVerifying,
    isUsingChatGptAccount,
    isWorkspaceOnline,
    normalizedUserCode,
    personalAccessDescription,
    workspaceAccessDescription,
  };
}

/**
 * The cloud-VM-backed Claude account section, split out of
 * ClaudeAuthenticationOptions so that function's own mutually-exclusive render
 * branches stay under the complexity gate (FOR-2605 follow-up).
 */
